'use client';

import type React from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import type { ProjectFeature } from '../../data/project-data';

const SECTION_ID = 'project-features';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

function getIcon(iconName: string) {
  const icons = LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const Icon = icons[iconName];
  return Icon || LucideIcons.Home;
}

interface ProjectFeaturesSectionProperties {
  features: ProjectFeature[];
  projectName: string;
}

function ProjectFeaturesSection({
  features,
  projectName,
}: ProjectFeaturesSectionProperties) {
  return (
    <SectionBase id={SECTION_ID} background="bg-card">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="KEY FEATURES"
          title={`What Makes ${projectName} Special`}
          description="Discover the standout features and custom details that define this home."
        />

        <div className={cls`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full`}>
          {features.map((feature) => {
            const Icon = getIcon(feature.icon);
            return (
              <motion.div
                key={feature.title}
                className={cls`flex flex-col gap-3 p-6 rounded-lg bg-background`}
                {...cardMotionProps}
              >
                <Icon className="text-primary size-8" />
                <TypographyH4 className="text-foreground">
                  {feature.title}
                </TypographyH4>
                <TypographySmall className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </TypographySmall>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default ProjectFeaturesSection;
